import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const MembershipContext = createContext();

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

// Mock membership service functions
const membershipService = {
  fetchMembershipPlans: async () => {
    // Mock data for membership plans
    return [
      {
        id: 'basic',
        name: 'Basic Plan',
        price: 999,
        duration_days: 90,
        application_limit: 5,
        description: 'Perfect for students applying to a few colleges',
        features: [
          'Up to 5 applications',
          'No service charges',
          'Basic study materials',
          'Email support'
        ]
      },
      {
        id: 'premium',
        name: 'Premium Plan',
        price: 1999,
        duration_days: 180,
        application_limit: 15,
        description: 'Ideal for students with multiple application needs',
        features: [
          'Up to 15 applications',
          'No service charges',
          'Complete study materials',
          'Previous year papers',
          'Priority support'
        ]
      },
      {
        id: 'unlimited',
        name: 'Unlimited Plan',
        price: 2999,
        duration_days: 365,
        application_limit: null,
        description: 'Best value for unlimited applications',
        features: [
          'Unlimited applications',
          'No service charges',
          'Complete study materials',
          'Previous year papers',
          'Expert counseling',
          '24/7 support'
        ]
      }
    ];
  },

  getUserMembership: async (userId) => {
    // Mock user membership data
    return null; // No active membership by default
  },

  subscribeToPlan: async (userId, planId, paymentDetails) => {
    // Mock subscription process
    const plans = await membershipService.fetchMembershipPlans();
    const plan = plans.find(p => p.id === planId);
    
    if (!plan) {
      throw new Error('Plan not found');
    }

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 1000));

    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + plan.duration_days);

    return {
      id: `membership_${Date.now()}`,
      user_id: userId,
      plan_id: planId,
      membership_plans: plan,
      applications_used: 0,
      applications_limit: plan.application_limit,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      payment_details: paymentDetails
    };
  },

  recordApplicationUsage: async (membershipId, orderId) => {
    // Mock recording application usage
    await new Promise(resolve => setTimeout(resolve, 500));
    return true;
  }
};

export const MembershipProvider = ({ children }) => {
  const { user } = useAuth();
  const [membership, setMembership] = useState(null);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch membership plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const plans = await membershipService.fetchMembershipPlans();
        setMembershipPlans(plans);
      } catch (error) {
        console.error('Error fetching membership plans:', error);
        // Set empty array as fallback
        setMembershipPlans([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Fetch user's active membership if logged in
  useEffect(() => {
    const fetchUserMembership = async () => {
      if (!user) {
        setMembership(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const userMembership = await membershipService.getUserMembership(user.id);
        setMembership(userMembership);
      } catch (error) {
        console.error('Error fetching user membership:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserMembership();
  }, [user]);

  // Subscribe to a membership plan
  const subscribeToPlan = async (planId, paymentDetails) => {
    if (!user) {
      toast.error('Please log in to subscribe to a plan');
      return null;
    }
    try {
      const newMembership = await membershipService.subscribeToPlan(user.id, planId, paymentDetails);
      setMembership(newMembership);
      toast.success('Successfully subscribed to the plan!');
      return newMembership;
    } catch (error) {
      toast.error(error.message || 'Failed to subscribe to plan');
      return null;
    }
  };

  // Check if a user can use their membership for an application
  const canUseForApplication = () => {
    if (!membership) return false;
    
    // Check if membership is active
    const now = new Date();
    const endDate = new Date(membership.end_date);
    if (now > endDate) return false;
    
    // Check if within application limit
    if (membership.applications_limit !== null && membership.applications_used >= membership.applications_limit) {
      return false;
    }
    
    return true;
  };

  // Record application usage
  const useForApplication = async (orderId) => {
    if (!membership) {
      return false;
    }
    try {
      await membershipService.recordApplicationUsage(membership.id, orderId);
      
      // Update local state
      setMembership(prev => ({
        ...prev,
        applications_used: prev.applications_used + 1
      }));
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to record application usage');
      return false;
    }
  };

  const getMembershipDetails = () => {
    try {
      if (!membership) return null;
      
      const { membership_plans, applications_used, applications_limit, start_date, end_date } = membership;
      
      return {
        plan: membership_plans,
        applicationsUsed: applications_used,
        applicationsLimit: applications_limit,
        startDate: start_date,
        endDate: end_date,
        remainingApplications: applications_limit === null ? 'Unlimited' : (applications_limit - applications_used)
      };
    } catch (error) {
      console.error('Error getting membership details:', error);
      return null;
    }
  };

  const value = {
    membership,
    membershipPlans,
    loading,
    subscribeToPlan,
    canUseForApplication,
    useForApplication,
    getMembershipDetails
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};